import { InternalLogicError } from '@/errors/InternalLogicError';
import {
  AllFilter,
  isSearchTreeLeafNode,
  isTemplatePlaceholder,
  NoneFilter,
  SearchFilter,
  SearchTreeLeafNode,
  SearchTreeNode,
  TemplatePlaceholderWithFilterContext,
  TestOnlyContactIdsRangeFilter,
} from '@/types';
/**
 * Updates all placeholder fields in the search tree identified by the leaf node
 * ids and the filterNameAndFields.
 *
 * Reason for it being in place: It worked out easier to do this way, and it
 * only gets called by createSearchTemplateJson() and which clones the tree, so
 * mutation isn't a problem.
 *
 * Reason for being exported if its only used here: Was written prior to
 * createSearchTemplateJson() and had its own tests, I didn't want to refactor
 * the tests.
 */
export function updatePlaceholdersInSearchTreeInPlace(
  searchTree: SearchTreeNode,
  placeholders: Record<string, TemplatePlaceholderWithFilterContext>
): void {
  const placeholderKeys = Object.keys(placeholders);

  if (placeholderKeys.length === 0) {
    return;
  }

  const uniqueLeafNodeIds = Array.from(new Set(placeholderKeys.map((key) => key.split(':')[0])));

  const leafNodesToUpdate = walkTreeAndGetLeafNodeReferences(searchTree, uniqueLeafNodeIds);

  for (const key of placeholderKeys) {
    const leafNodeId = key.split(':')[0];
    const nodeToUpdate = leafNodesToUpdate[leafNodeId];
    const placeholderWithContext = placeholders[key];

    if (nodeToUpdate) {
      updatePlaceholderFieldInLeafNode(nodeToUpdate, placeholderWithContext);
    } else {
      throw new InternalLogicError(
        `Could not find leaf node with id ${leafNodeId} for placeholder update. This should not happen.`
      );
    }
  }
}

function walkTreeAndGetLeafNodeReferences(
  node: SearchTreeNode,
  nodeIds: string[]
): Record<string, SearchTreeLeafNode> {
  if (isSearchTreeLeafNode(node)) {
    if (node._leafNodeId && nodeIds.includes(node._leafNodeId)) {
      return { [node._leafNodeId]: node };
    }
    return {};
  }

  let foundNodes: Record<string, SearchTreeLeafNode> = {};
  for (const childNode of node.nodes) {
    const childResults = walkTreeAndGetLeafNodeReferences(childNode, nodeIds);
    foundNodes = { ...foundNodes, ...childResults };
  }

  return foundNodes;
}

/**
 * updatePlaceholderFieldInLeafNode should only get called with search filters
 * that have a value field
 */
function assertIsAppropriateSearchFilter(
  filter: SearchFilter
): asserts filter is Exclude<SearchFilter, TestOnlyContactIdsRangeFilter | NoneFilter | AllFilter> {
  if (!('value' in filter)) {
    throw new InternalLogicError(
      "Filter missing value. This means its TestOnlyContactIdsRangeFilter, NoneFilter or AllFilter which shouldn't happen"
    );
  }
}

function updatePlaceholderFieldInLeafNode(
  node: SearchTreeLeafNode,
  placeholderWithFilterContext: TemplatePlaceholderWithFilterContext
) {
  assertIsAppropriateSearchFilter(node.filter);

  const { filterNameAndField, placeholder } = placeholderWithFilterContext;

  const keyPath = filterNameAndField.split('.');

  // The very first one is just the name of the filter
  if (keyPath[0] !== node.filter.name) {
    throw new Error(
      `Filter name mismatch: node filter name = '${node.filter.name}' but payload filterNameAndField = '${filterNameAndField}'`
    );
  }

  if (keyPath.length === 1) {
    if (!isTemplatePlaceholder(node.filter.value)) {
      console.info({
        node,
        filterNameAndField,
      });
      throw new InternalLogicError(
        'updatePlaceholderFieldInLeafNode() passed node and filterNameAndField combination that references a non-placeholder field'
      );
    }

    // This non-compound filter that has no nested fields
    node.filter.value = {
      placeholder: {
        ...placeholder,
      },
    };
  } else {
    // Otherwise we need to drill down.

    // We don't want to get the value of the very last field, we want to update
    // it in place so we stop at its parent
    const indexOfFieldParent = keyPath.length - 1;

    let current: unknown = node.filter.value;
    // We skip the first one, since that is the filter name
    for (let i = 1; i <= indexOfFieldParent; i++) {
      if (i === indexOfFieldParent) {
        // We're at the parent of the field, so we update in place
        const nestedFilter = current as Record<string, unknown>;

        if (!isTemplatePlaceholder(nestedFilter[keyPath[indexOfFieldParent]])) {
          console.info({
            node,
            filterNameAndField,
          });
          throw new InternalLogicError(
            'updatePlaceholderFieldInLeafNode() passed node and filterNameAndField combination that references a non-placeholder field'
          );
        }
        nestedFilter[keyPath[indexOfFieldParent]] = {
          placeholder: {
            ...placeholder,
          },
        };
      } else if (current && typeof current === 'object' && keyPath[i] in current) {
        current = (current as Record<string, unknown>)[keyPath[i]];
      } else {
        throw new Error(`Invalid key path: ${keyPath.join('.')}`);
      }
    }
  }
}
